import { feedKey } from '../lib/feedContext';
/**
 * Как показывать деньги: валюта площадки на ноге, режим — только у прибыли ленты.
 *
 * Расчёт строки по-прежнему в рублях банка (`lib/row.ts`). Здесь только экран
 * и буфер. В чужую форму человек вбивает то, что принимает касса: рубли у
 * ру-конторы, доллары у зарубежной, центы и шеры у биржи. Показать рубли
 * банка на BC.Game или Polymarket — отправить его считать курс в уме на
 * скорости (23.08, владелец).
 *
 * Прибыль ленты и карточки — режимом из сита (`₽` / `$` / `%`). Суммы ног
 * от этого режима не зависят: рубли у ру-конторы, доллары у зарубежной,
 * центы и шеры у биржи (без рублей банка).
 */

import { create } from 'zustand';

import { sharesShown } from '../lib/calc';
import type { ExchangeStake, RowLeg } from '../lib/row';
import { isRubCur } from '../lib/row';
import { rub, usd } from './format';
import type { Meta } from './meta';
import { venueKind } from './meta';

export type ShowUnit = 'usd' | 'rub' | 'pct';

export const SHOW_UNITS = ['usd', 'rub', 'pct'] as const;

export const SHOW_NAMES: Record<ShowUnit, string> = {
  usd: '$',
  rub: '₽',
  pct: '%',
};

const KEY = feedKey('forq.next.show');

function read(): ShowUnit {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === 'usd' || raw === 'rub' || raw === 'pct') return raw;
  } catch {
    // Приватный режим — не повод падать.
  }
  return 'usd';
}

export const useShow = create<{
  unit: ShowUnit;
  setUnit: (unit: ShowUnit) => void;
}>((set) => ({
  unit: read(),
  setUnit: (unit) => {
    try {
      localStorage.setItem(KEY, unit);
    } catch {
      // Приватный режим — не повод падать.
    }
    set({ unit });
  },
}));

export function isRubLeg(leg: Pick<RowLeg, 'unit' | 'cur'>): boolean {
  return leg.unit === 'money' && isRubCur(leg.cur);
}

/**
 * Рублёвая ставка на экране: полка «рублёвые», а не то, что нога случайно
 * привезла в `cur`. Thunderpick в каталоге зарубежный — в форму идут доллары,
 * даже если кадр подписал ногу рублём.
 */
export function isRubStake(
  leg: Pick<RowLeg, 'unit' | 'cur' | 'source' | 'ownCur'>,
  meta?: Meta | null,
): boolean {
  if (leg.unit === 'share') return false;
  if (leg.ownCur === 'RUB') return true;
  if (leg.ownCur === 'USD') return false;
  if (meta != null) {
    const kind = venueKind(meta, leg.source);
    if (kind === 'rub') return true;
    if (kind === 'out' || kind === 'exchange') return false;
  }
  return isRubLeg(leg);
}

/** Валюта кассы по каталогу, без ручного переключения. */
export function catalogCur(
  leg: Pick<RowLeg, 'unit' | 'cur' | 'source'>,
  meta?: Meta | null,
): 'RUB' | 'USD' {
  return isRubStake({ ...leg, ownCur: undefined }, meta) ? 'RUB' : 'USD';
}

export function hasRubBook(
  legs: readonly Pick<RowLeg, 'unit' | 'cur' | 'source'>[],
  meta?: Meta | null,
): boolean {
  return legs.some((leg) => isRubStake(leg, meta));
}

/** Сколько спишет площадка в долларах. `null` — курса нет, считать нечем. */
export function stakeUsd(leg: Pick<RowLeg, 'unit' | 'usd' | 'rub' | 'lay' | 'layStake'>, fx: number): number | null {
  if (leg.usd != null) return leg.usd;
  if (!(fx > 0)) return null;
  const raw = leg.lay ? (leg.layStake ?? leg.rub) : leg.rub;
  return raw / fx;
}

/**
 * Что вводят в форму площадки. У ноги «против» — ставка соперника, не риск.
 * Без знака и пробелов: поле суммы чужое и такое не примет.
 * Биржа — шеры в купон. Поле калькулятора держит баксы, см. `editAmount`.
 */
export function forForm(leg: RowLeg, fx: number, meta?: Meta | null): string {
  if (leg.unit === 'share') return (leg.shares ?? 0).toFixed(1);
  const raw = leg.lay ? (leg.layStake ?? leg.rub) : leg.rub;
  if (isRubStake(leg, meta) || !(fx > 0)) return String(Math.round(raw));
  const dollars = stakeUsd(leg, fx);
  return (dollars ?? raw / fx).toFixed(2);
}

/** Та же сумма числом: её кладёт подтверждатель в чужое поле. */
export function formStake(leg: RowLeg, fx: number, meta?: Meta | null): number {
  return Number(forForm(leg, fx, meta));
}

/**
 * Число в поле суммы калькулятора. На бирже по умолчанию баксы расхода:
 * человек ставит «2$ на полике», а шеры едут за живой ценой. `as === 'share'`
 * держит штуки, как в купоне. В буфер по-прежнему уходят шеры из `forForm`.
 */
export function editAmount(
  leg: RowLeg,
  fx: number,
  meta?: Meta | null,
  as?: ExchangeStake,
): string {
  if (leg.unit === 'share') {
    if (as === 'share') return (leg.shares ?? 0).toFixed(1);
    const dollars = stakeUsd(leg, fx);
    return (dollars ?? 0).toFixed(2);
  }
  return forForm(leg, fx, meta);
}

/** Замок суммы, когда есть только рублёвый объём, без готовой ноги. */
export function lockForm(
  amountRub: number,
  leg: Pick<RowLeg, 'unit' | 'cur' | 'cents' | 'shares' | 'source'>,
  fx: number,
  meta?: Meta | null,
  as?: ExchangeStake,
): string {
  if (leg.unit === 'share') {
    if (as === 'share') {
      if (leg.cents != null && leg.cents > 0 && fx > 0) {
        return (amountRub / fx / (leg.cents / 100)).toFixed(1);
      }
      return (leg.shares ?? 0).toFixed(1);
    }
    if (!(fx > 0)) return '0.00';
    return (amountRub / fx).toFixed(2);
  }
  return formAmount(amountRub, leg, fx, meta);
}

/** Какой единицей читать биржевой замок: своя, иначе настройка. */
export function lockAsOf(
  lock: { source: string; as?: ExchangeStake } | null | undefined,
  source: string,
  fallback: ExchangeStake,
): ExchangeStake {
  return lock?.source === source && (lock.as === 'usd' || lock.as === 'share')
    ? lock.as
    : fallback;
}

/** Переписать набитое число из баксов в шеры или обратно, по живой цене. */
export function convertStakeForm(
  form: string,
  from: ExchangeStake,
  to: ExchangeStake,
  cents: number | null,
): string {
  if (from === to) return form;
  const raw = form.trim().replace(/\s/g, '').replace(',', '.');
  const n = Number.parseFloat(raw);
  const price = (cents ?? 0) / 100;
  if (!(n > 0) || !(price > 0)) return form;
  if (from === 'usd') return sharesShown(String(n / price)).toFixed(1);
  return (n * price).toFixed(2);
}

export function stakeLockBody(
  forkId: string,
  source: string,
  form: string,
  unit: RowLeg['unit'],
  as?: ExchangeStake,
): { forkId: string; source: string; form: string; as?: ExchangeStake } {
  if (unit === 'share') return { forkId, source, form, as: as === 'share' ? 'share' : 'usd' };
  return { forkId, source, form };
}

/** Сумма в форму площадки, когда рублёвый объём не из раскладки ноги. */
export function formAmount(
  amountRub: number,
  leg: Pick<RowLeg, 'unit' | 'cur' | 'cents' | 'shares' | 'source'>,
  fx: number,
  meta?: Meta | null,
): string {
  if (leg.unit === 'share') {
    if (leg.cents != null && leg.cents > 0 && fx > 0) {
      return (amountRub / fx / (leg.cents / 100)).toFixed(1);
    }
    return (leg.shares ?? 0).toFixed(1);
  }
  if (isRubStake(leg, meta) || !(fx > 0)) return String(Math.round(amountRub));
  return (amountRub / fx).toFixed(2);
}

/** Прибыль строки ленты — тем режимом, который выбран в сите. */
export function profitText(profitRub: number, roiPct: number, fx: number, unit: ShowUnit): string {
  if (unit === 'pct') {
    const sign = roiPct < 0 ? '−' : '+';
    return `${sign}${Math.abs(roiPct).toFixed(2)} %`;
  }
  if (unit === 'rub' || !(fx > 0)) {
    return `${profitRub < 0 ? '' : '+'}${rub(profitRub)} ₽`;
  }
  return `${profitRub < 0 ? '' : '+'}${usd(profitRub / fx)} $`;
}

/**
 * Вложенное и возврат карточки: доллары, плюс рубли если в вилке есть ру-нога.
 * Прибыль сюда не кладём — она живёт режимом сита (`profitText`).
 */
export function cardMoney(
  amountRub: number,
  fx: number,
  withRub: boolean,
): { main: string; extra: string | null } {
  if (!(fx > 0)) return { main: `${rub(amountRub)} ₽`, extra: null };
  return {
    main: `${usd(amountRub / fx)} $`,
    extra: withRub ? `${rub(amountRub)} ₽` : null,
  };
}

export function cardSay(one: { main: string; extra: string | null }): string {
  return one.extra === null ? one.main : `${one.main} · ${one.extra}`;
}

/** Сумма в валюте ЭТОЙ ноги: то, что вбивают на её площадке. */
export function venueMoney(
  amountRub: number,
  leg: Pick<RowLeg, 'unit' | 'cur' | 'cents' | 'source'>,
  fx: number,
  meta?: Meta | null,
): string {
  if (leg.unit === 'share') {
    if (leg.cents != null && leg.cents > 0 && fx > 0) {
      return `${(amountRub / fx / (leg.cents / 100)).toFixed(1)} шер`;
    }
    return fx > 0 ? `${usd(amountRub / fx)} $` : `${rub(amountRub)} ₽`;
  }
  if (isRubStake(leg, meta) || !(fx > 0)) return `${rub(amountRub)} ₽`;
  return `${usd(amountRub / fx)} $`;
}

/** Крупное конторской ноги: рубли у ру-БК, доллары у зарубежной. */
export function moneyAmount(leg: RowLeg, fx: number, meta?: Meta | null): string {
  const raw = leg.lay ? (leg.layStake ?? 0) : leg.rub;
  if (isRubStake(leg, meta) || !(fx > 0)) return `${rub(raw)} ₽`;
  const dollars = stakeUsd(leg, fx);
  return `${usd(dollars ?? raw / fx)} $`;
}
